import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentCompany } from './useCurrentCompany';
import { supabase } from '../lib/supabase';
import { useToast } from '../components/ui/Toast';

/**
 * Hook to check if Google Review Enhancement is enabled for the current company
 */
export function useGoogleReviewEnhancementToggle() {
  const { data: currentCompanyId } = useCurrentCompany();

  return useQuery<boolean>({
    queryKey: ['googleReviewEnhancementToggle', currentCompanyId],
    enabled: !!currentCompanyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    queryFn: async () => {
      if (!currentCompanyId) {
        return false;
      }

      const { data, error } = await supabase
        .from('companies')
        .select('enable_google_review_enhancement')
        .eq('id', currentCompanyId)
        .single();

      if (error) {
        console.error('Error fetching Google Review Enhancement toggle:', error);
        return false;
      }

      // Type assertion needed until database types are regenerated
      return ((data as unknown as { enable_google_review_enhancement?: boolean | null })?.enable_google_review_enhancement ?? false) as boolean;
    },
  });
}

/**
 * Hook to toggle Google Review Enhancement on/off
 */
export function useToggleGoogleReviewEnhancement() {
  const { data: currentCompanyId } = useCurrentCompany();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!currentCompanyId) {
        throw new Error('No company selected');
      }

      // Type assertion needed until database types are regenerated
      const { error } = await supabase
        .from('companies')
        .update({ enable_google_review_enhancement: enabled } as any)
        .eq('id', currentCompanyId);

      if (error) {
        throw error;
      }

      return enabled;
    },
    onSuccess: (enabled) => {
      queryClient.invalidateQueries({ queryKey: ['googleReviewEnhancementToggle', currentCompanyId] });
      showToast(
        enabled
          ? 'Google Review Enhancement enabled'
          : 'Google Review Enhancement disabled',
        'success',
      );
    },
    onError: (error) => {
      console.error('Error toggling Google Review Enhancement:', error);
      showToast('Failed to update setting. Please try again.', 'error');
    },
  });
}
